import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import InstallPrompt from './components/InstallPrompt';
import SplashScreen from './components/SplashScreen';
import Landing from './pages/Landing';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import JoinGroup from './pages/JoinGroup';
import Dashboard from './pages/Dashboard';
import CreateGroup from './pages/CreateGroup';
import GroupDetails from './pages/GroupDetails';
import InviteMembers from './pages/InviteMembers';
import Profile from './pages/Profile';
import UpiSetup from './pages/UpiSetup';
import VerifyContributions from './pages/VerifyContributions';
import Delegations from './pages/Delegations';
import GroupNotes from './pages/GroupNotes';
import Leaderboard from './pages/Leaderboard';
import Expenses from './pages/Expenses';
import GroupChat from './pages/GroupChat';
import Analytics from './pages/Analytics';
import Gamification from './pages/Gamification';
import LendingPool from './pages/LendingPool';
import BiometricSettings from './pages/BiometricSettings';
import Referral from './pages/Referral';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Support from './pages/Support';
import Guide from './pages/Guide';
// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminGroups from './pages/admin/AdminGroups';
import AdminFinance from './pages/admin/AdminFinance';
import AdminLogs from './pages/admin/AdminLogs';
import AdminSettings from './pages/admin/AdminSettings';
import AdminManagement from './pages/admin/AdminManagement';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [splashComplete, setSplashComplete] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    
    // Check for saved admin session
    const savedAdminToken = localStorage.getItem('adminToken');
    const savedAdminUser = localStorage.getItem('adminUser');
    if (savedAdminToken && savedAdminUser) {
      setAdminToken(savedAdminToken);
      setAdminUser(JSON.parse(savedAdminUser));
    }
    
    // Check for saved theme
    const savedTheme = JSON.parse(savedUser || '{}')?.theme;
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    document.documentElement.classList.remove('dark');
  };

  const handleUpdateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleAdminLogin = (admin, token) => {
    setAdminUser(admin);
    setAdminToken(token);
  };

  const handleAdminLogout = () => {
    setAdminUser(null);
    setAdminToken(null);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  };

  return (
    <HelmetProvider>
      <SplashScreen onComplete={() => setSplashComplete(true)} />
      <div className="App">
        <BrowserRouter>
          <AppRouter 
            user={user} 
            adminUser={adminUser}
            adminToken={adminToken}
            handleLogin={handleLogin}
            handleLogout={handleLogout}
            handleUpdateUser={handleUpdateUser}
            handleAdminLogin={handleAdminLogin}
            handleAdminLogout={handleAdminLogout}
          />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
        <InstallPrompt />
      </div>
    </HelmetProvider>
  );
}

// Separate component to handle hash-based auth detection
function AppRouter({ user, adminUser, adminToken, handleLogin, handleLogout, handleUpdateUser, handleAdminLogin, handleAdminLogout }) {
  const location = useLocation();
  
  // Check URL hash for session_id (Emergent Auth callback)
  // This handles the case when user returns from Google OAuth
  const hash = window.location.hash || '';
  const hasSessionId = hash.includes('session_id=') || 
                       location.hash?.includes('session_id=') ||
                       window.location.search?.includes('session_id=');
  
  if (hasSessionId) {
    return <AuthCallback onLogin={handleLogin} />;
  }

  return (
    <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
          <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />} />
          <Route path="/auth/callback" element={<AuthCallback onLogin={handleLogin} />} />
          <Route path="/auth/google/callback" element={<AuthCallback onLogin={handleLogin} />} />
          <Route path="/signup" element={user ? <Navigate to="/dashboard" /> : <Signup onLogin={handleLogin} />} />
          <Route path="/verify-email" element={<VerifyEmail onLogin={handleLogin} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/join/:groupId" element={<JoinGroup user={user} onLogin={handleLogin} />} />
          
          {/* Public Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/support" element={<Support user={user} onLogout={handleLogout} />} />
          <Route path="/guide" element={<Guide />} />
          
          <Route path="/dashboard" element={user ? <Dashboard user={user} onLogout={handleLogout} onUpdateUser={handleUpdateUser} /> : <Navigate to="/login" />} />
          <Route path="/create-group" element={user ? <CreateGroup user={user} /> : <Navigate to="/login" />} />
          <Route path="/group/:groupId" element={user ? <GroupDetails user={user} /> : <Navigate to="/login" />} />
          <Route path="/invite/:groupId" element={user ? <InviteMembers user={user} /> : <Navigate to="/login" />} />
          <Route path="/upi-setup/:groupId" element={user ? <UpiSetup user={user} /> : <Navigate to="/login" />} />
          <Route path="/verify/:groupId" element={user ? <VerifyContributions user={user} /> : <Navigate to="/login" />} />
          <Route path="/delegations/:groupId" element={user ? <Delegations user={user} /> : <Navigate to="/login" />} />
          <Route path="/group-notes/:groupId" element={user ? <GroupNotes user={user} /> : <Navigate to="/login" />} />
          <Route path="/expenses/:groupId" element={user ? <Expenses user={user} /> : <Navigate to="/login" />} />
          <Route path="/chat/:groupId" element={user ? <GroupChat user={user} /> : <Navigate to="/login" />} />
          <Route path="/analytics/:groupId" element={user ? <Analytics user={user} /> : <Navigate to="/login" />} />
          <Route path="/leaderboard" element={user ? <Leaderboard user={user} /> : <Navigate to="/login" />} />
          <Route path="/gamification" element={user ? <Gamification user={user} /> : <Navigate to="/login" />} />
          <Route path="/lending/:groupId" element={user ? <LendingPool user={user} /> : <Navigate to="/login" />} />
          <Route path="/biometric-settings" element={user ? <BiometricSettings user={user} onUpdateUser={handleUpdateUser} /> : <Navigate to="/login" />} />
          <Route path="/referral" element={user ? <Referral user={user} /> : <Navigate to="/login" />} />
          <Route path="/profile" element={user ? <Profile user={user} onLogout={handleLogout} onUpdateUser={handleUpdateUser} /> : <Navigate to="/login" />} />
          
          {/* Admin Routes - Hidden from regular users, only accessible via direct URL */}
          <Route path="/admin/login" element={adminUser ? <Navigate to="/admin/dashboard" /> : <AdminLogin onAdminLogin={handleAdminLogin} />} />
          <Route path="/admin" element={!adminUser ? <Navigate to="/admin/login" /> : <AdminLayout admin={adminUser} token={adminToken} onAdminLogout={handleAdminLogout} />}>
            <Route index element={<Navigate to="/admin/dashboard" />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="groups" element={<AdminGroups />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="logs" element={<AdminLogs />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="admins" element={<AdminManagement />} />
          </Route>
        </Routes>
  );
}

export default App;
